import React, { useEffect, useMemo, useRef, useState } from "react";

import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import ClearIcon from "@material-ui/icons/Clear";
import SendIcon from "@material-ui/icons/Send";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  row: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  thumb: {
    width: 72,
    height: 72,
    flex: "none",
    borderRadius: 6,
    objectFit: "cover",
    backgroundColor: theme.palette.action.hover,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  fields: {
    flex: 1,
    minWidth: 0,
  },
  hiddenInput: {
    display: "none",
  },
}));

const canCaption = (file) => !String(file.type || "").startsWith("audio/");

/**
 * Review step before sending attachments: one row per file, each with its own
 * comment. Files can be added (picker or paste) or removed before sending.
 */
const MediaPreviewDialog = ({ medias, loading, onAdd, onRemove, onCancel, onSend }) => {
  const classes = useStyles();
  const [captions, setCaptions] = useState(() => new Map());
  const addInputRef = useRef(null);

  const previews = useMemo(
    () =>
      medias.map((file) =>
        String(file.type || "").startsWith("image/")
          ? URL.createObjectURL(file)
          : null
      ),
    [medias]
  );

  useEffect(
    () => () => previews.forEach((url) => url && URL.revokeObjectURL(url)),
    [previews]
  );

  const setCaption = (file, value) =>
    setCaptions((prev) => new Map(prev).set(file, value));

  const handlePaste = (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (!files.length) return;
    e.preventDefault();
    onAdd(files);
  };

  const handleSend = () =>
    onSend(medias.map((file) => (captions.get(file) || "").trim()));

  return (
    <Dialog
      open
      onClose={loading ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
      onPaste={handlePaste}
    >
      <DialogTitle>
        {medias.length === 1
          ? "Enviar archivo"
          : `Enviar ${medias.length} archivos`}
      </DialogTitle>
      <DialogContent dividers>
        {medias.map((file, index) => (
          <div className={classes.row} key={`${file.name}-${file.lastModified}-${index}`}>
            {previews[index] ? (
              <img src={previews[index]} alt={file.name} className={classes.thumb} />
            ) : (
              <div className={classes.thumb}>
                <InsertDriveFileIcon color="action" />
              </div>
            )}
            <div className={classes.fields}>
              <Typography variant="caption" color="textSecondary" noWrap component="div">
                {file.name}
              </Typography>
              {canCaption(file) && (
                <TextField
                  fullWidth
                  multiline
                  maxRows={3}
                  margin="dense"
                  variant="outlined"
                  placeholder="Comentario (opcional)"
                  autoFocus={index === 0}
                  disabled={loading}
                  value={captions.get(file) || ""}
                  onChange={(e) => setCaption(file, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey && medias.length === 1) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                />
              )}
            </div>
            <IconButton
              size="small"
              aria-label="Quitar archivo"
              disabled={loading}
              onClick={() => onRemove(index)}
            >
              <ClearIcon />
            </IconButton>
          </div>
        ))}
        <Typography variant="caption" color="textSecondary" component="div" style={{ marginTop: 8 }}>
          Puede pegar más capturas aquí (Ctrl+V) o agregarlas con el botón.
        </Typography>
      </DialogContent>
      <DialogActions>
        <input
          multiple
          type="file"
          ref={addInputRef}
          className={classes.hiddenInput}
          onChange={(e) => {
            onAdd(Array.from(e.target.files || []));
            e.target.value = "";
          }}
        />
        <Button
          startIcon={<AttachFileIcon />}
          disabled={loading}
          onClick={() => addInputRef.current && addInputRef.current.click()}
        >
          Agregar más
        </Button>
        <div style={{ flex: 1 }} />
        <Button onClick={onCancel} disabled={loading}>
          Cancelar
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={loading}
          onClick={handleSend}
          endIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
        >
          Enviar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MediaPreviewDialog;
